/**
 * Dr. Mani's Magic Quiz — shared types.
 *
 * Source of truth: dr-mm-files/dmm-quiz-spec.json (the v3 spec).
 * These types mirror the spec shapes verbatim so the data modules and the
 * logic engine stay faithful to it.
 */

export type QuestionType = 'single_select' | 'multi_select';
export type InterstitialType = 'screen' | 'loading';

/** Motivational tap-to-continue screen between quiz sections. */
export interface QuizScreen {
  id: string;
  type: 'screen';
  order: number;
  title: string;
  description: string;
  countsTowardProgress: false;
}

/** Auto-advancing loading screen before the email gate. */
export interface QuizLoading {
  id: string;
  type: 'loading';
  order: number;
  caption: string;
  title: string;
  subtitle?: string;
  description?: string;
  autoAdvanceMs: number;
  countsTowardProgress: false;
}

/**
 * UX-6 pre-reveal build-up: a staged 3-4 second sequence (lines fading in over
 * a seed mark growing stage by stage) between the last answer and the gate.
 * It is anticipation, not a loading screen: it runs even if results are instant.
 */
export interface QuizBuildUp {
  id: string;
  type: 'buildup';
  order: number;
  countsTowardProgress: false;
}

export type QuizStep = QuizQuestion | QuizScreen | QuizLoading | QuizBuildUp;

export function isQuestionStep(step: QuizStep): step is QuizQuestion {
  return step.type === 'single_select' || step.type === 'multi_select';
}

export type NeedKey = 'LIFE' | 'FOOD' | 'HOME' | 'FOOD + LIFE' | 'ALL';
export type DmmSegmentKey = 'vegetables' | 'flowers' | 'lawn' | 'houseplants' | 'fruit_trees' | 'everything';

/** A selectable option on a quiz question. Extra fields are tags the logic engine reads. */
export interface QuestionOption {
  value: string;
  label: string;
  sublabel?: string;
  /** Brand line icon key (ICON MAP tab), rendered by DrManiIcon. */
  icon?: string;
  /** Side-effects to apply when this option is chosen, e.g. { has_lawn: true }. */
  sets?: Record<string, unknown>;
  /** Human-readable description of the branch behavior (doc only). */
  branch?: string;
  archetype_lean?: string;
  segment?: string;
  environment?: 'outdoor' | 'indoor';
  /** Soil/add-on ingredients this setup implies (e.g. coco_coir, rice_hulls). */
  adds?: string[];
  /** Per-option dosing hints surfaced on the question screen. */
  psb?: string;
  ckaa?: string;
  super_soil?: string;
  flags?: string[];
  why_tag?: string;
  archetype_override?: string;
  /** q7 struggles tag with a need category; PEST/PROOF are diagnosis-only, not regimen needs. */
  need?: NeedKey | 'PEST' | 'PROOF';
  villain_key?: string;
  /** Display condition, e.g. "show only if has_lawn". Parsed by the orchestrator. */
  conditional?: string;
}

export interface QuizQuestion {
  id: string;
  order: number;
  type: QuestionType;
  /** True when an option on this question changes which screens come next. */
  branch?: boolean;
  auto_advance?: boolean;
  writes_field: string;
  kicker: string;
  question: string;
  /** Optional intro line above the question (Q1 compressed header). */
  lede?: string;
  /** Honest completion contract shown under the intro line. */
  contract?: string;
  instruction?: string;
  note?: string;
  /** Whole-question display condition, e.g. "show only if has_lawn == true". */
  conditional?: string;
  options: QuestionOption[];
}

export interface Archetype {
  id: string;
  name: string;
  triggered_by: string;
  core_reading: string;
  core_reading_lines: string[];
  superpower: string;
  blind_spot: string;
}

export interface Profile {
  id: string;
  name: string;
  triggered_by: string;
  headline: string;
  description: string;
  regimen_text: string;
  need: NeedKey;
  pain_tag: string;
}

export interface KeyInsight {
  why_value: string;
  why_tag: string;
  key_insight: string;
  key_insight_lines: string[];
}

export interface DmmSegmentReview {
  setup: string;
  quote: string;
  reviewer: string;
  product: string;
  verification: string;
}

export interface DmmProductVariant {
  sizeLabel: string;
  variantId: string;
  imageUrl: string;
}

export interface DmmProductSizing {
  sizeLabel: string;
  quantity: string;
  cadence: string;
  duration: string;
  dose: string;
  cardCopy: string;
  variantId?: string;
  variantImageUrl?: string;
  subscriptionBridge: string;
}

export interface PillarSnippet {
  role: string;
  short: string;
  long?: string;
}

/** Purchasable product keys that have a Shopify URL on drmanismagic.com. */
export type ProductKey =
  | 'plant_super_boost'
  | 'crab_kelp_amino_acids'
  | 'super_soil'
  | 'the_bundle';

/** Supplementary add-on keys referenced by regimen_modifiers (no Shopify URL). */
export type AddOnKey =
  | 'rice_hulls'
  | 'coco_coir'
  | 'biochar'
  | 'chameleon_sprayer'
  | 'diatomaceous_earth'
  | 'sulfur_dust'
  | 'castile_soap';

export interface DoseRule {
  source: string;
  rules: Record<string, string>;
  conversions?: Record<string, string>;
  analysis?: string;
}

export interface Dosing {
  crab_kelp_amino_acids: DoseRule;
  plant_super_boost: DoseRule;
  super_soil: DoseRule;
}

/** A resolved product in the final regimen, with its dose line for the user's answers. */
export interface RegimenItem {
  key: ProductKey;
  name: string;
  role: string;
  snippet: string;
  dose: string;
  productUrl: string;
  sizing?: DmmProductSizing;
  variantId?: string;
  variantImageUrl?: string;
  /** UX-2: the "because" match line built from the person's answers. */
  becauseLine?: string;
}

/** A supplementary add-on surfaced as a secondary, shoppable product card. */
export interface RegimenAddOn {
  key: AddOnKey;
  name: string;
  note: string;
  handle: string;
  productUrl: string;
}

export interface ResolvedRegimen {
  items: RegimenItem[];
  /** When the primary rec is the_bundle, the three purchasable pillar products inside it. */
  bundlePillars?: RegimenItem[];
  addOns: RegimenAddOn[];
  generosityLine: string;
}
